// gibsImageryProvider.js — Ticket 02: satellite imagery layer (NASA GIBS).
//
// One `ImageryProvider` (see `./ImageryProvider.js`) implementation backed by NASA's Global
// Imagery Browse Services (GIBS) — a free, keyless, daily-global raster tile service.
//
// Layer/tileMatrixSet choice: `VIIRS_SNPP_CorrectedReflectance_TrueColor` — VIIRS/Suomi-NPP
// true-color corrected-reflectance, GIBS' standard "satellite basemap" layer (same family
// NASA Worldview defaults to). Resolution is ~375m/pixel; GIBS documents
// `GoogleMapsCompatible_Level9` (305.7m/pixel-equivalent web-mercator tile grid, 9 zoom
// levels) as the matching Web Mercator TileMatrixSet for that resolution class — confirmed
// against the layer's own `<TileMatrixSetLink>` in GIBS's public WMTS GetCapabilities
// (`https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/1.0.0/WMTSCapabilities.xml`, spot-
// checked live 2026-07-22 from this sandbox — see verification notes in the ticket report).
//
// URL template (REST/XYZ access pattern, per
// https://nasa-gibs.github.io/gibs-api-docs/access-basics/#generic-xyz-tile-access-tms):
//
//   https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/{layer}/default/{time}/{tileMatrixSet}/{z}/{y}/{x}.{ext}
//
// Note the tile-index order is `{z}/{y}/{x}` (GIBS' WMTS REST convention is
// TileMatrix/TileRow/TileCol, i.e. z/row/col — "row" is the y-index), NOT OL's usual
// `{z}/{x}/{y}`. `source/XYZ`'s `url` option is a plain string template substituted by OL
// itself, so we just author the template with `{y}` before `{x}` to match GIBS' expected
// order; no custom `tileUrlFunction` needed.
//
// Live verification performed from this sandbox (network access was available):
//   - GET .../VIIRS_SNPP_CorrectedReflectance_TrueColor/default/2026-03-14/GoogleMapsCompatible_Level9/2/1/2.jpg
//     -> HTTP 200, ~19KB JPEG (a real in-range date).
//   - GET .../default/2030-01-01/... -> HTTP 404 (future date, no data yet).
//   - GET .../default/2010-01-01/... -> HTTP 404 (before this layer's granule history starts).
//   - Fetched the live WMTS GetCapabilities XML and confirmed this layer's `<Dimension>` time
//     domain includes contiguous `.../P1D` ranges covering all of ticket 01's demo DTG window
//     (2026-03-01..2026-03-31), and its `<TileMatrixSetLink>` is exactly
//     `GoogleMapsCompatible_Level9`.
// So: URL params below are confirmed correct against the live service as of 2026-07-22, not
// just inferred from docs. GIBS' actual per-layer time domain is a set of disjoint date
// ranges (visible gaps, e.g. mid-2022 and mid-2024 outages) rather than one contiguous
// [start, end] span — this provider's `getAvailableDateRange`/coverage check deliberately
// collapses that to one conservative bound (see `DEFAULT_AVAILABLE_START_MS`/`_END_MS` below)
// rather than fetching/parsing the full disjoint-range list on every call. That's a
// reasonable stub for "daily-global" coverage purposes per the ticket, but a consumer relying
// on exact per-day availability outside the demo window should do a live DescribeDomains/
// GetCapabilities check instead.

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { assertImageryProvider } from './ImageryProvider';

dayjs.extend(utc);

export const GIBS_BASE_URL = 'https://gibs.earthdata.nasa.gov/wmts/epsg3857/best';
export const GIBS_DEFAULT_LAYER = 'VIIRS_SNPP_CorrectedReflectance_TrueColor';
export const GIBS_DEFAULT_TILE_MATRIX_SET = 'GoogleMapsCompatible_Level9';
export const GIBS_DEFAULT_FORMAT = 'jpg';

// Conservative, hand-verified availability bound for the default layer (see header comment).
// Comfortably spans ticket 01's demo DTG window (2026-03-01..2026-03-31) with margin in both
// directions. Exported so tests/consumers can reason about (or override via options) the
// exact bound without re-deriving it.
export const GIBS_DEFAULT_AVAILABLE_START_MS = Date.parse('2024-11-06T00:00:00.000Z');
export const GIBS_DEFAULT_AVAILABLE_END_MS = Date.parse('2026-07-22T00:00:00.000Z');

// GIBS' EPSG:3857 service covers the standard Web Mercator lat extent; a bbox entirely
// outside it (e.g. deep polar) has no imagery regardless of date.
const WEB_MERCATOR_MAX_LAT = 85.06;

function toMs(dtg) {
    if (typeof dtg === 'number') return dtg;
    if (dtg instanceof Date) return dtg.getTime();
    if (dtg && typeof dtg.valueOf === 'function') {
        const v = dtg.valueOf();
        if (typeof v === 'number' && !Number.isNaN(v)) return v;
    }
    const parsed = Date.parse(dtg);
    return Number.isNaN(parsed) ? NaN : parsed;
}

function bboxWithinGlobalExtent(bbox) {
    if (!bbox) return true; // unknown bbox: don't let extent-checking block coverage
    const [, minLat, , maxLat] = bbox;
    if (!Number.isFinite(minLat) || !Number.isFinite(maxLat)) return true;
    return maxLat > -WEB_MERCATOR_MAX_LAT && minLat < WEB_MERCATOR_MAX_LAT;
}

function buildTileUrlTemplate({ baseUrl, layer, tileMatrixSet, format, time }) {
    // Tile index order intentionally {y}/{x} — see header comment.
    return `${baseUrl}/${layer}/default/${time}/${tileMatrixSet}/{z}/{y}/{x}.${format}`;
}

/**
 * Build a GIBS-backed `ImageryProvider` (see `./ImageryProvider.js` for the interface shape).
 *
 * @param {object} [options]
 * @param {string} [options.id] - provider id, defaults to `'gibs'`.
 * @param {string} [options.baseUrl] - defaults to `GIBS_BASE_URL`.
 * @param {string} [options.layer] - GIBS layer identifier, defaults to
 *   `GIBS_DEFAULT_LAYER` (VIIRS/SNPP true-color corrected reflectance).
 * @param {string} [options.tileMatrixSet] - defaults to `GIBS_DEFAULT_TILE_MATRIX_SET`.
 * @param {string} [options.format] - tile file extension, defaults to `GIBS_DEFAULT_FORMAT`.
 * @param {number} [options.availableStartMs] - lower bound (epoch ms) of the "has coverage"
 *   date stub, defaults to `GIBS_DEFAULT_AVAILABLE_START_MS`.
 * @param {number} [options.availableEndMs] - upper bound (epoch ms), defaults to
 *   `GIBS_DEFAULT_AVAILABLE_END_MS`.
 * @returns {import('./ImageryProvider').ImageryProvider}
 */
export function createGibsImageryProvider(options = {}) {
    const {
        id = 'gibs',
        baseUrl = GIBS_BASE_URL,
        layer = GIBS_DEFAULT_LAYER,
        tileMatrixSet = GIBS_DEFAULT_TILE_MATRIX_SET,
        format = GIBS_DEFAULT_FORMAT,
        availableStartMs = GIBS_DEFAULT_AVAILABLE_START_MS,
        availableEndMs = GIBS_DEFAULT_AVAILABLE_END_MS,
    } = options;

    function getCoverage(bbox, dtg) {
        const ms = toMs(dtg);
        const withinDateRange = Number.isFinite(ms) && ms >= availableStartMs && ms <= availableEndMs;
        const withinExtent = bboxWithinGlobalExtent(bbox);
        const hasCoverage = withinDateRange && withinExtent;
        if (!hasCoverage) return { tileUrlTemplate: null, hasCoverage: false };

        // Bug fix (timezone-conversion day-off-by-one report): GIBS' daily tile URLs are keyed by
        // UTC calendar date — `dayjs(ms).format(...)` without `.utc()` renders `ms`'s LOCAL
        // calendar day instead, silently fetching the wrong day's imagery whenever the local
        // timezone isn't UTC (exactly the failure mode reported for the DTG table elsewhere in
        // this map-editor feature).
        const time = dayjs(ms).utc().format('YYYY-MM-DD');
        return {
            tileUrlTemplate: buildTileUrlTemplate({ baseUrl, layer, tileMatrixSet, format, time }),
            hasCoverage: true,
        };
    }

    // ponytail: GIBS is daily-global for this layer family — rather than issuing a live
    // DescribeDomains request per AOI (network call, disjoint-range parsing) this returns the
    // same fixed stub range regardless of `bbox`, per the ticket's explicitly-sanctioned
    // "simple stub" option. See header comment for why this is a collapsed/conservative bound
    // rather than the layer's true disjoint availability set.
    function getAvailableDateRange(_bbox) {
        return { start: availableStartMs, end: availableEndMs };
    }

    return assertImageryProvider({
        id,
        getCoverage,
        getAvailableDateRange,
    });
}

// Shared default instance — the one MapEditorModal wires up. Tests / call sites that want
// custom bounds (e.g. deterministic date ranges) should call `createGibsImageryProvider(...)`
// directly instead.
export const gibsImageryProvider = createGibsImageryProvider();

export default gibsImageryProvider;
